import { FC, useContext, useEffect, useState } from "react";
import Blockly, { MenuOption } from "blockly/core";
import {
  BlocklyWorkspace,
  ToolboxDefinition,
  WorkspaceSvg,
} from "react-blockly";
// import Theme from "@blockly/theme-modern";
import { pythonGenerator } from "blockly/python";
import { IBlocklyProps } from "../types";
import workspaceConfiguration from "./workspaceConfiguration.json";
import { Screen } from "../helpers/Screen";
import { FileSystem } from "../helpers/FileSystem";
import cn from "classnames";
import { LanguageContext } from "../../../contexts/languageContext";

import * as Tr from "blockly/msg/tr";

import { FieldGridDropdown } from "@blockly/field-grid-dropdown";

import "../styles/microbit.css";
import { ImageProperties } from "blockly/core/field_dropdown";

import { ledImages } from "./ledImages";

function createGridItems(): MenuOption[] {
  const items: MenuOption[] = [];
  const length = ledImages.length;

  for (let i = 0; i < length; i++) {
    const value = `${ledImages[i].alt}`;

    // ImageProperties tipinde nesne oluşturuyoruz
    const image: ImageProperties = {
      src: ledImages[i].src,
      width: 40,
      height: 40,
      alt: ledImages[i].alt,
    };

    items.push([image, value]); // `MenuOption` tipine uygun format
  }
  return items;
}

const urlParams = new URLSearchParams(window.location.search);
var ApiUrl = process.env.REACT_APP_CDN_URL;
if (window.location.hostname.includes("editor-staging.riders.ai")) {
  ApiUrl = process.env.REACT_APP_STAGING_CDN_URL;
}

if (
  window.location.hostname.includes("localhost") &&
  urlParams.has("localcdn")
) {
  ApiUrl = "/cdn";
}

export const BEditor: FC<IBlocklyProps> = (props) => {
  const { className, setCode, setXML, project, file_path } = props;
  const { refreshKey, theme } = useContext<any>(Screen.context);

  const setDarkThemeTextColor = () => {
    const labels = document.querySelectorAll(".blocklyTreeLabel");
    for (let i = 0; i < labels.length; i++) {
      if (theme === "dark") {
        labels[i].classList.add("blocklyTreeLabelDark");
      } else {
        labels[i].classList.remove("blocklyTreeLabelDark");
      }
    }
  };

  const { readFile } = useContext<any>(FileSystem.context);

  const [userXML, setUserXML] = useState<string | undefined>(undefined);

  const [hover, setHover] = useState(false);

  const { language } = useContext<any>(LanguageContext);

  const blocklyChangeEvent = new CustomEvent("blocklyChange", {
    detail: {
      message: "Blockly değişti",
      time: new Date(),
      // Diğer istediğiniz bilgiler...
    },
  });

  useEffect(() => {
    setUserXML(undefined);
    readFile(file_path).then((code: string) => {
      setUserXML(code);
    });
  }, [refreshKey, file_path, theme]);

  const [toolboxCategories, setToolboxCategories] = useState<
    any | ToolboxDefinition
  >({
    kind: "categoryToolbox",
    contents: [],
  }); // Empty initial toolbox, if it will not find toolboxCategories.json

  const workspaceChange = (workspace: WorkspaceSvg) => {
    window.getBlocklyWorkspace = (CB: any) => {
      CB(workspace);
    };

    var code = pythonGenerator.workspaceToCode(workspace);
    setCode(code);
    try {
      const _panel: SVGAElement | null = document.querySelector(
        "#blockly_wrapper > div > div > svg:nth-child(7)"
      );
      if (_panel && _panel.style.display === "none")
        (document.querySelector(
          "#blockly_wrapper > div > div > svg:nth-child(8)"
        ) as SVGAElement)!.style.transform = "";
    } catch (ignored) {}
    //setDarkThemeTextColor();
  };

  const onXmlChange = (newXML: string) => {
    setXML(newXML);
    window.dispatchEvent(blocklyChangeEvent);
  };

  const [scriptLoaded, setScriptLoaded] = useState(false);

  const blockClrs: any = {
    Temel: {
      colour: "#1E90FF",
      cssConfig: {
        container: "blocklyTreeIcon_basic",
      },
    },
    Girdi: {
      colour: "#D401D4",
      cssConfig: {
        container: "blocklyTreeIcon_input",
      },
    },
    Müzik: {
      colour: "#E63023",
      cssConfig: {
        container: "blocklyTreeIcon_music",
      },
    },
    LED: {
      colour: "#5C2C91",
      cssConfig: {
        container: "blocklyTreeIcon_led",
      },
    },
    Radyo: {
      colour: "#E3008C",
      cssConfig: {
        container: "blocklyTreeIcon_radio",
      },
    },
    Döngüler: {
      colour: "#05AA02",
      cssConfig: {
        container: "blocklyTreeIcon_loop",
      },
    },
    Mantık: {
      colour: "#01A4A6",
      cssConfig: {
        container: "blocklyTreeIcon_logic",
      },
    },
    Değişkenler: {
      colour: "#DC143C",
      cssConfig: {
        container: "blocklyTreeIcon_variable",
      },
    },
    Matematik: {
      colour: "#9400D3",
      cssConfig: {
        container: "blocklyTreeIcon_math",
      },
    },
  };

  useEffect(() => {
    (async () => {
      let data = null;

      if (!project?.toolboxPath) {
        const file = await readFile(".blockly/toolbox.json");
        data = JSON.parse(file);
      } else {
        data = await fetch(
          `${ApiUrl}/${project.toolboxPath.replace(
            "{language}",
            language.toLowerCase()
          )}`
        ).then((res) => res.json());
      }

      setToolboxCategories({
        kind: "categoryToolbox",
        contents: data.contents.map((item: any) => {
          return {
            ...item,
            colour: blockClrs[item.name]?.colour,
            cssConfig: blockClrs[item.name]?.cssConfig,
          };
        }),
      });
    })();
  }, [project]);

  useEffect(() => {
    async function fetchBlockData() {
      try {
        // Make Blockly related modules available in the global scope
        const Blockly = await import("blockly");
        const BlocklyPython = await import("blockly/python");

        // Eğer `FieldGridDropdown` Blockly'ye kayıtlı değilse, kaydedelim
        if (
          !Blockly.registry.hasItem(
            Blockly.registry.Type.FIELD,
            "field_grid_dropdown"
          )
        ) {
          Blockly.registry.register(
            Blockly.registry.Type.FIELD,
            "field_grid_dropdown",
            FieldGridDropdown
          );
        }

        window.Blockly = Blockly;
        window.BlocklyPython = BlocklyPython.pythonGenerator;

        window.Blockly.FieldCheckbox.CHECK_CHAR = "    ";

        Blockly.Blocks["grid_dropdown_block"] = {
          init: function () {
            this.appendDummyInput()
              .appendField("Simgeyi göster: ")
              .appendField(
                new FieldGridDropdown(createGridItems(), undefined, {
                  columns: 5,
                }),
                "GRID_SELECTION"
              );

            this.setPreviousStatement(true, null);
            this.setNextStatement(true, null);
            this.setColour("#1E90FF");
            this.setTooltip("5x8 görsellerle grid dropdown.");
            this.setHelpUrl("");
          },
        };

        // Get BlockGenerator script
        let initializerURL = null;
        let generatorURL = null;

        if (project.folderUrl) {
          initializerURL = project.folderUrl + "initblocks.js";
          generatorURL = project.folderUrl + "generateblocks.js";
          if (project.toolboxPath.includes("{language}")) {
            const path = project.toolboxPath.split("/").slice(0, -1).join("/");
            initializerURL =
              ApiUrl +
              "/" +
              path.replace("{language}", language.toLowerCase()) +
              "/initblocks.js";
            generatorURL =
              ApiUrl +
              "/" +
              path.replace("{language}", language.toLowerCase()) +
              "/generateblocks.js";
          }
        }

        const scripts = [
          document.createElement("script"),
          document.createElement("script"),
          document.createElement("script"),
        ];

        if (initializerURL && generatorURL) {
          scripts[0].src =
            "https://cdnjs.cloudflare.com/ajax/libs/google-closure-library/20230802.0.0/base.min.js";
          scripts[1].src = initializerURL;
          scripts[2].src = generatorURL;

          // Only first script's "onload" function defined
          // on second script's "onload" function, we check if both scripts are loaded
          scripts[1].onload = async () => {
            for (let i = 0; i < 40; i++) {
              if (window.blockly_generate_ready && window.blockly_init_ready) {
                setScriptLoaded(true);
                break;
              }
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          };
        } else {
          const initText = await readFile(".blockly/initblocks.js");
          const generateText = await readFile(".blockly/generateblocks.js");
          scripts[0].type = "text/javascript";
          scripts[0].text = initText;
          scripts[1].type = "text/javascript";
          scripts[1].text = generateText;
          scripts[1].type = "text/javascript";
          scripts[1].text = generateText;

          setScriptLoaded(true);
        }

        document.body.appendChild(scripts[0]);
        document.body.appendChild(scripts[1]);
        document.body.appendChild(scripts[2]);
      } catch (error) {
        console.error(error);
      }
    }
    fetchBlockData();
  }, [project]);

  useEffect(() => {
    if (window.blocklyThemeLoaded) return;

    if (language === "TR") {
      Blockly.setLocale(Tr);
    }

    Blockly.Theme.defineTheme("microbit", {
      name: "microbit",
      base: Blockly.Themes.Classic,
      componentStyles: {
        workspaceBackgroundColour: theme === "dark" ? "#2E3B4C" : "#fafafa",
        toolboxBackgroundColour: theme === "dark" ? "#2E3B4C" : "#fff",
        toolboxForegroundColour: "#000",
        flyoutBackgroundColour: "#ddd",
        flyoutForegroundColour: "#000",
        flyoutOpacity: 0.75,
        scrollbarColour: "#1e1e1e",
        scrollbarOpacity: 0.6,
      },
      fontStyle: {},
      startHats: false,
      categoryStyles: {
        logic_category: {
          colour: "#FF0000", // Yeni renk (kırmızı)
        },
        loops_category: {
          colour: "#00FF00", // Yeni renk (yeşil)
        },
      },
    });

    window.blocklyThemeLoaded = true;
  }, []);

  if (!scriptLoaded || !project) {
    return (
      <div className={className} id="blockly_wrapper">
        <div className="h-full flex flex-col justify-center items-center">
          <span className="mb-6 text-2xl">Loading Block Coding</span>
          <div style={{ display: "flex" }}>
            <div className="dot dot--one"></div>
            <div className="dot dot--two"></div>
            <div className="dot dot--three"></div>
            <div className="dot dot--four"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(className, "microbit", {
        hoverBlockly: true,
      })}
      id="blockly_wrapper"
      key={refreshKey}
    >
      {userXML && (
        <BlocklyWorkspace
          className="h-full"
          toolboxConfiguration={toolboxCategories}
          workspaceConfiguration={{
            media: workspaceConfiguration.media,
            renderer: "zelos",
            zoom: {
              ...workspaceConfiguration.zoom,
              startScale: 0.75,
            },
            theme: "microbit",
            grid: {
              spacing: 20,
              length: 3,
              colour: theme === "dark" ? "#fff" : "#ccc",
              snap: true,
            },
            move: {
              scrollbars: {
                horizontal: true,
                vertical: true,
              },
              drag: true,
              wheel: true,
            },
          }}
          initialXml={userXML}
          onInject={workspaceChange}
          onWorkspaceChange={workspaceChange}
          onXmlChange={onXmlChange}
        />
      )}
    </div>
  );
};
